import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { useGetPieceQuery } from '@api/piece';
import { PieceAction } from '@apiContract';

import { useFinishPieceMutation } from '../../hooks/useFinishPieceMutation';
import { useValidationExitGuard } from '../../hooks/useValidationExitGuard';
import { ActionComponentProps } from '../../index';
import { loadingDialogSX } from './styles';
import useNotifyComment from './NotifyComment/useNotifyComment';
import NotifyMairoomComment from './NotifyComment';

export const FinishMail = ({ mailpieces, actionStarted, onActionFinish, validationData }: ActionComponentProps) => {
  const { guardedExit, exitConfirmDialog } = useValidationExitGuard(validationData, onActionFinish);

  const { runAction, isLoading: actionInProgress } = useFinishPieceMutation();

  const [trackingNumber, setTrackingNumber] = useState('');

  const { values, changeValues } = useNotifyComment();

  const handleChangeTrackingNumber = (e: React.ChangeEvent<HTMLInputElement>) => setTrackingNumber(e.target.value);
  const { data, isLoading } = useGetPieceQuery(mailpieces[0].id);

  const hasNoMailTo = !isLoading && !data?.mailTo;

  useEffect(() => {
    if (hasNoMailTo) {
      onActionFinish();
    }
  }, [hasNoMailTo, onActionFinish]);

  const confirmFinishMail = async () => {
    await runAction({
      action: PieceAction.FinishMail,
      pieces: mailpieces,
      comment: values.comment,
      notifyMailroom: values.notifyMailroom,
      skipNotifyRecipient: values.skipNotifyRecipient,
      validationData,
      additionalData: { trackingNumber },
    });

    onActionFinish();
  };

  if (isLoading) {
    return (
      <>
        <Dialog maxWidth="sm" open={true} onClose={guardedExit}>
          <Box sx={loadingDialogSX}>
            <CircularProgress size={40} />
          </Box>
        </Dialog>

        {exitConfirmDialog}
      </>
    );
  }

  if (hasNoMailTo || !data?.mailTo) {
    return null;
  }

  const { addressLine1, addressLine2, administrativeArea, locality, postalCode, countryCode } = data.mailTo;

  return (
    <Dialog maxWidth="sm" open={actionStarted} onClose={actionInProgress ? () => {} : guardedExit}>
      <DialogTitle sx={{ p: 3 }}>{mailpieces.length === 1 ? 'Mail item' : 'Mail selected items'}</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={3}>
          <Typography variant="body1" color="text.secondary">
            {mailpieces.length === 1 ? (
              <>Please mail this item to the following address, and enter the tracking number if applicable.</>
            ) : (
              <>
                Please mail <strong>{mailpieces.length}</strong> item(s) to the following address, and enter the
                tracking number if applicable.
              </>
            )}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            <strong>
              {`${mailpieces[0].recipient?.displayName || ''}`}
              <br />
              {addressLine1}
              <br />
              {!!addressLine2 && (
                <>
                  {addressLine2}
                  <br />
                </>
              )}
              {`${locality} ${administrativeArea} ${postalCode}`}
              <br />
              {countryCode}
            </strong>
          </Typography>
          <TextField
            value={trackingNumber}
            onChange={handleChangeTrackingNumber}
            placeholder="Tracking Number"
            fullWidth
          />
        </Stack>

        <NotifyMairoomComment values={values} onChange={changeValues} />
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={guardedExit} disabled={actionInProgress}>
          Cancel
        </Button>
        <Button
          onClick={confirmFinishMail}
          loading={actionInProgress}
          startIcon={actionInProgress && <SaveIcon />}
          loadingPosition={actionInProgress ? 'start' : undefined}
          variant="contained"
          color="primary"
        >
          Complete
        </Button>
      </DialogActions>

      {exitConfirmDialog}
    </Dialog>
  );
};
